/**
 * MyDataExportPort — the member data-portability seam (033 US5, T041).
 *
 * `requestExport` produces the signed-in member's export
 * (`member.myData.export`, contracts/my-data-api.md): a machine-readable
 * JSON artifact plus a human-readable rendering of the app-owned
 * categories, with legacy-owned categories carried as references only
 * (FR-014/FR-015).
 *
 * SHAPE NOTE — inline artifacts, not an `artifactRef`
 * ==================================================
 *
 * The contract sketches `Promise<{ artifactRef: string }>` with "large
 * exports may be async with a ready signal". The API ships the
 * SYNCHRONOUS INLINE path instead: an `artifactRef` implies object
 * storage, and the 033 feature must not own the object-storage port
 * (FR-027; architecture invariant 3 forbids `@aws-sdk` anywhere under
 * `audit-and-compliance/**`). So the artifacts come back inline and this
 * port hands them to the caller, which materializes them as Blob
 * downloads with no server round-trip. The async/ready-signal path is
 * deferred until an object-storage owner exists outside that module.
 *
 * `format` is the `'json'` PLACEHOLDER pending feature 10 (FR-016) — an
 * explicit placeholder, not a settled choice.
 *
 * Adapters: `TrpcMyDataExportAdapter` (production); tests inject fakes.
 * Follows the `AccountDeletionPort` port-hook-adapter convention.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md.
 */

/** The export format shipped today — the FR-016 placeholder. */
export const EXPORT_FORMAT = 'json' as const;

/** One downloadable artifact, returned inline (no object storage). */
export interface MyDataExportArtifact {
  readonly filename: string;
  readonly mimeType: string;
  readonly contents: string;
}

/** Both FR-014 renderings of one read. */
export interface MyDataExportResult {
  readonly format: typeof EXPORT_FORMAT;
  readonly generatedAt: string;
  readonly machineReadable: MyDataExportArtifact;
  readonly humanReadable: MyDataExportArtifact;
}

export interface MyDataExportPort {
  /**
   * Produce the export for the signed-in member. Rejects on failure; the
   * consumer renders one retryable error state.
   *
   * There is deliberately no `ExportUnavailableError` sibling to
   * `ErasureUnavailableError`. That one exists because erasure's API refuses
   * for a real business reason (PRECONDITION_FAILED while the retention
   * parameter is unconfigured). The export needs no parameter and so has no
   * refusal code, which leaves no production path to a distinct "unavailable"
   * state: a missing `member.myData` namespace is a composition bug the
   * adapter raises loudly, not a member-facing condition.
   */
  requestExport(): Promise<MyDataExportResult>;
}
